import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const sectionAnim = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const faqs = [
  {
    id: 'faq-return',
    q: 'Is 12% annual return guaranteed?',
    a: 'No. The 12% return is a historical average for diversified Indian equity mutual funds over long periods (15-20+ years). Actual returns vary year to year. This calculator uses it as a planning benchmark, not a guarantee.',
  },
  {
    id: 'faq-inflation',
    q: 'Why is 6% used for inflation?',
    a: "India's average consumer inflation has been around 5-7% over the past two decades. We use 6% as a realistic planning assumption. Your actual expenses may vary based on lifestyle, healthcare costs, and location.",
  },
  {
    id: 'faq-3x',
    q: 'What does "3x corpus" mean?',
    a: 'When your remaining corpus reaches three times your initial investment, it indicates strong wealth sustainability. At this point, your investments are generating enough returns to comfortably cover inflation-adjusted withdrawals.',
  },
  {
    id: 'faq-taxes',
    q: 'Should I consider taxes in this calculation?',
    a: 'This is a pre-tax illustration. In practice, returns are subject to capital gains taxes which would reduce the effective return rate. Consult a tax advisor for after-tax planning.',
  },
];

export const Rule126Section = () => {
  return (
    <section id="rule-12-6" className="py-12 sm:py-16 lg:py-20 bg-background" data-testid="education-12-6-rule-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...sectionAnim} className="text-center mb-10">
          <Badge variant="outline" className="mb-3 text-xs font-medium">
            <Info size={12} className="mr-1" />
            Understanding the Math
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">The 12-6 Rule Explained</h2>
          <p className="mt-2 text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Understanding the interplay between growth and inflation is key to long-term wealth planning.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Explanation */}
          <motion.div {...sectionAnim}>
            <div className="space-y-6">
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                The <span className="font-semibold text-primary">12-6 Rule</span> is a practical framework
                for understanding how your wealth interacts with two opposing forces over time:
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Card className="border border-green-200 bg-green-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={18} className="text-green-600" />
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Growth</Badge>
                    </div>
                    <div className="font-mono-num text-3xl font-bold text-green-700">12%</div>
                    <p className="text-xs text-green-700/80 mt-1">
                      Average annual return on diversified equity investments in India
                    </p>
                  </CardContent>
                </Card>

                <Card className="border border-amber-200 bg-amber-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown size={18} className="text-amber-600" />
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Inflation</Badge>
                    </div>
                    <div className="font-mono-num text-3xl font-bold text-amber-700">6%</div>
                    <p className="text-xs text-amber-700/80 mt-1">
                      Average annual increase in cost of living in India
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="rounded-xl bg-secondary/60 p-4 border border-border">
                <h4 className="text-sm font-semibold text-primary mb-2">How It Works</h4>
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 mt-0.5">1.</span>
                    Your corpus grows at 12% annually through returns on investment.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">2.</span>
                    Your withdrawals increase at 6% annually to maintain purchasing power.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">3.</span>
                    The net real growth is approximately 6% {'\u2014'} but the compounding effect of rising withdrawals gradually catches up.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">4.</span>
                    The key question: <span className="font-semibold">How long before withdrawals overtake growth?</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* FAQs */}
          <motion.div {...sectionAnim}>
            <Card className="border border-border shadow-md">
              <CardContent className="p-5 sm:p-6">
                <h3 className="text-sm font-semibold text-primary mb-4">Frequently Asked Questions</h3>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-sm text-left font-medium hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <div className="mt-6 rounded-xl bg-primary p-4">
                  <h4 className="text-sm font-semibold text-primary-foreground mb-1">Example Scenario</h4>
                  <p className="text-xs text-primary-foreground/80 leading-relaxed">
                    With a {'₹'}1 Crore corpus and {'₹'}6 Lakh annual withdrawal ({'₹'}50,000/month), your corpus
                    reaches 3x in about 19 years. The power of compounding at 12% significantly outpaces
                    the 6% inflation-adjusted withdrawal in the early decades.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Rule126Section;
